import { ISearchResult } from "./Models/ISearchResult";
import { KeyHelper } from "./KeyHelper";
import { ISearchResultTag } from "./Models/ISearchResultTag";
import { Guid } from "./Guid";
import { ITag } from "./Models/ITag";
import { ITask } from "./Models/ITask";

import { SpecialCause } from "./SpecialCause";
import { TimeSource } from "./TimeSource";
import { DataLayer } from "./TheClaw";

export class TaskHelper {
	constructor(private readonly dataLayer: DataLayer, private readonly timeSource: TimeSource) { }

	// HACK: This is clearly crap, as it doesn't deal with tags
	public SearchResultAsTask(aResult: ISearchResult) {
		return this.updateTaskFromSearchResult(aResult);
	}

	public ObjectIsTask(obj: unknown): obj is ITask {
		return typeof obj === "object" && obj !== null && "externalId" in obj && "integrationGuid" in obj && "name" in obj;
	}

	public async TaskifySearchResult(aResult: ISearchResult, specialCause?: SpecialCause) {
		const taskKey = KeyHelper.GetSearchResultTaskKey(aResult);

		const existingTask = this.dataLayer.Tasks.Get(taskKey);
		const aTask = this.updateTaskFromSearchResult(aResult, existingTask);
		this.dataLayer.Tasks.Set(taskKey, aTask, "TaskHelper", specialCause);

		const taskMetadata = this.dataLayer.TaskMetadatas.Get(taskKey);
		if (!taskMetadata)
			this.dataLayer.TaskMetadatas.Set(
				taskKey,
				{
					externalId: aResult.externalId,
					integrationGuid: aTask.integrationGuid,
					isFavorite: false,
					usageCount: 1,
					isTodo: false,
					createdWhen: this.timeSource.GetUtcTime(),
					lastUpdatedWhen: this.timeSource.GetUtcTime(),
				},
				"TaskHelper",
				specialCause
			);
		const tags = await Promise.all(
			aResult.tags.map((tag) => {
				return this.tagFromSearchResultTag(
					tag,
					aTask.integrationGuid,
					this.dataLayer.Tags.Get(KeyHelper.GetSearchResultTagKey(tag, aTask.integrationGuid))
				);
			})
		);
		const existingTags = this.dataLayer.TaskTagLinks.All().filter(
			(x) => KeyHelper.GetTaskTagLinkTaskKey(x) === taskKey
		);
		for (const tag of tags) {
			this.dataLayer.Tags.Set(KeyHelper.GetTagKey(tag), tag, "TaskHelper", specialCause);
			if (!existingTags.some((x) => x.tagExternalId === tag.externalId)) {
				const newLink = {
					integrationGuid: tag.integrationGuid,
					tagExternalId: tag.externalId,
					taskExternalId: aTask.externalId,
				};
				this.dataLayer.TaskTagLinks.Set(
					KeyHelper.GetTaskTagLinkKey(newLink),
					newLink,
					"TaskHelper",
					specialCause
				);
			}
		}
		const tagLinksToDelete = existingTags.filter((x) => !tags.some((y) => y.externalId === x.tagExternalId));
		for (const tagLinkToDelete of tagLinksToDelete) {
			this.dataLayer.TaskTagLinks.Remove(
				KeyHelper.GetTaskTagLinkKey(tagLinkToDelete),
				"TaskHelper",
				specialCause
			);
		}
		return aTask;
	}

	public SearchResultifyTask(aTask: ITask) {
		const tagLinks = this.dataLayer.TaskTagLinks.All().filter(
			(tt) => tt.integrationGuid == aTask.integrationGuid && tt.taskExternalId == aTask.externalId
		);
		const tags = this.dataLayer.Tags.All().filter(
			(tag) => tag.integrationGuid == aTask.integrationGuid && tagLinks.some((tl) => tl.tagExternalId == tag.externalId)
		);

		const aResult: ISearchResult = {
			...aTask,
			tags: tags.toArray(),
		};

		// console.log("tags: ", aResult.tags)
		return aResult;
	}

	private tagFromSearchResultTag(t: ISearchResultTag, integrationGuid: Guid, existingTag?: ITag): ITag {
		const retval =
			existingTag ||
			({
				integrationGuid,
				externalId: t.externalId,
				createdWhen: this.timeSource.GetUtcTime(),
			} as ITag);

		retval.lastUpdatedWhen = this.timeSource.GetUtcTime();
		retval.tagTypeCodeName = t.tagTypeCodeName;
		retval.value = t.value;

		return retval;
	}

	private updateTaskFromSearchResult(aResult: ISearchResult, existingTask?: ITask): ITask {
		const retval =
			existingTask ||
			({
				integrationGuid: aResult.integrationGuid,
				externalId: aResult.externalId,
				externalCreatedWhen: aResult.externalCreatedWhen,
				createdWhen: this.timeSource.GetUtcTime(),
			} as ITask);

		retval.name = aResult.name;
		retval.status = aResult.status;
		retval.dueWhen = aResult.dueWhen;
		retval.assignee = aResult.assignee;
		retval.description = aResult.description;
		retval.linkUrl = aResult.linkUrl;

		retval.metadata = JSON.stringify({
			// TODO: task metadata
		});

		if (!existingTask || JSON.stringify(existingTask) !== JSON.stringify(retval)) {
			retval.lastUpdatedWhen = this.timeSource.GetUtcTime();
		}

		return retval;
	}
}
